import { RoomContext, StartAudio, useParticipants, useRoomContext, useTextStream } from '@livekit/components-react';
import type { Room } from 'livekit-client';
import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';

import { useAgentIdentities, type AgentRegistry } from '../agents';
import { useStrings } from '../i18n/context';
import { readPref, writePref } from '../prefs';
import {
  COPILOT_ATTRIBUTE,
  DEFAULT_LISTENING_MODE,
  MODE_ATTRIBUTE,
  ROLE_ATTRIBUTE,
  ROLE_HOST,
  SUBTITLES_TOPIC,
  isListeningMode,
  type ListeningMode,
} from '../protocol';
import { LocalTimes } from '../local-times';
import { collectSubtitles } from '../streams';
import { ChatPanel } from './ChatPanel';
import { CopilotPanel } from './CopilotPanel';
import { ListenerAudio } from './ListenerAudio';
import { MeetingControls, type Panel } from './MeetingControls';
import { MeetingHeader } from './MeetingHeader';
import { NotesPanel } from './NotesPanel';
import { ReconnectingBanner } from './ReconnectingBanner';
import { TranscriptPanel } from './TranscriptPanel';
import { VideoGrid } from './VideoGrid';

interface Props {
  readonly room: Room;
  readonly agents: AgentRegistry;
  readonly code: string;
  /** vm.lang of this participant. */
  readonly language: string;
  readonly embedded: boolean;
}

function storedMode(): ListeningMode {
  const stored = readPref('mode');
  return isListeningMode(stored) ? stored : DEFAULT_LISTENING_MODE;
}

function RoomContent({ agents: registry, code, language, embedded }: Omit<Props, 'room'>) {
  const strings = useStrings();
  const room = useRoomContext();
  const agents = useAgentIdentities(registry);
  const participants = useParticipants();
  const [panel, setPanel] = useState<Panel | null>(null);
  const [mode, setMode] = useState<ListeningMode>(storedMode);
  // The meeting API marks the person who started the meeting (ADR 005); only they get the copilot.
  const isHost = room.localParticipant.attributes[ROLE_ATTRIBUTE] === ROLE_HOST;
  const [copilotOn, setCopilotOn] = useState(() => readPref('copilot') === 'on');
  const [chatTotal, setChatTotal] = useState(0);
  const [chatSeen, setChatSeen] = useState(0);

  // The interpreter's subtitles, read ONCE here (a topic takes one handler) and handed to the
  // tiles and the transcript, stamped with this device's time of receipt (local-times.ts).
  const { textStreams } = useTextStream(SUBTITLES_TOPIC);
  const localTimes = useMemo(() => new LocalTimes(), []);
  const collected = useMemo(() => collectSubtitles(textStreams, agents), [textStreams, agents]);
  useEffect(() => localTimes.update(collected), [localTimes, collected]);
  const lines = useSyncExternalStore(localTimes.subscribe, localTimes.getSnapshot);
  const names = useMemo(
    () => new Map(participants.map((participant) => [participant.identity, participant.name ?? ''])),
    [participants],
  );
  const listener = useMemo(() => ({ identity: room.localParticipant.identity, language }), [room, language]);

  // The mode is this device's preference and a participant attribute: the interpreter voices
  // a language only while someone listens to it in voice mode (ADR 001).
  useEffect(() => {
    writePref('mode', mode);
    room.localParticipant.setAttributes({ [MODE_ATTRIBUTE]: mode }).catch(() => undefined);
  }, [room, mode]);

  // The copilot runs while the host keeps it on, shown or hidden; the interpreter reads the attribute.
  useEffect(() => {
    if (!isHost) return;
    writePref('copilot', copilotOn ? 'on' : 'off');
    room.localParticipant.setAttributes({ [COPILOT_ATTRIBUTE]: copilotOn ? 'on' : 'off' }).catch(() => undefined);
  }, [room, isHost, copilotOn]);

  // Chat entries count as read while the chat is open; the count is kept when it closes.
  function showPanel(next: Panel | null) {
    if (panel === 'chat' || next === 'chat') setChatSeen(chatTotal);
    setPanel(next);
  }

  function togglePanel(next: Panel) {
    showPanel(panel === next ? null : next);
  }

  // The first press starts the copilot and shows it; later presses show or hide the panel.
  function pressCopilot() {
    if (!copilotOn) {
      setCopilotOn(true);
      showPanel('copilot');
    } else {
      togglePanel('copilot');
    }
  }

  return (
    <div className="meeting">
      <MeetingHeader code={code} embedded={embedded} agents={agents} />
      <ReconnectingBanner />
      <div className="meeting__body">
        <main className="meeting__stage" aria-labelledby="vm-room-label">
          <span id="vm-room-label" className="visually-hidden">
            {strings.appTitle}
          </span>
          <VideoGrid lines={lines} listener={listener} mode={mode} />
        </main>
        <TranscriptPanel open={panel === 'transcript'} lines={lines} language={language} names={names} onClose={() => showPanel(null)} />
        <ChatPanel
          open={panel === 'chat'}
          names={names}
          localIdentity={room.localParticipant.identity}
          onClose={() => showPanel(null)}
          onTotal={setChatTotal}
        />
        {isHost && (
          <CopilotPanel
            open={panel === 'copilot'}
            running={copilotOn}
            agents={agents}
            onHide={() => showPanel(null)}
            onToggleRunning={() => setCopilotOn((on) => !on)}
          />
        )}
        <NotesPanel open={panel === 'notes'} language={language} agents={agents} onClose={() => showPanel(null)} />
      </div>
      <MeetingControls
        panel={panel}
        onTogglePanel={togglePanel}
        mode={mode}
        onModeChange={setMode}
        chatUnread={panel === 'chat' ? 0 : Math.max(0, chatTotal - chatSeen)}
        {...(isHost ? { copilot: { running: copilotOn, onToggle: pressCopilot } } : {})}
      />
      <ListenerAudio listener={{ identity: room.localParticipant.identity, language, mode }} />
      <StartAudio label={strings.startAudio} className="button start-audio" />
    </div>
  );
}

export function MeetingRoom({ room, ...props }: Props) {
  return (
    <RoomContext.Provider value={room}>
      <RoomContent {...props} />
    </RoomContext.Provider>
  );
}
